import { UzFormField, UzInput } from '@utazzo/ui';

/**
 * Stage-indicator color picker - shared between QueryStatus and
 * QuerySubStatus form drawers (Masters CMS).
 *
 * Renders a row of preset swatches + a free-form hex input. NULL clears the
 * field so the UI falls back to bucket / name inference (see ADR-0032 §18 +
 * `apps/admin/src/features/leads/stage-color.ts`).
 *
 * The form must register a `color` field on its zod resolver (both
 * `CreateQueryStatusRequestSchema` / `CreateQuerySubStatusRequestSchema`
 * already do via `HexColorSchema.nullable().optional()`).
 */

const COLOR_PRESETS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '#3b82f6', label: 'Blue' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
  { value: '#a855f7', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#a1a1aa', label: 'Zinc' },
];

interface StageColorFieldProps {
  name?: string;
  disabled?: boolean;
  /** Optional hint shown below the swatch row. */
  hint?: string;
}

export const StageColorField = ({
  name = 'color',
  disabled,
  hint = 'Drives the stage chip / dot color throughout the leads UI. Leave blank to fall back to bucket / name inference.',
}: StageColorFieldProps): React.JSX.Element => (
  <UzFormField name={name}>
    {({ field, error }) => {
      const raw = (field.value as string | null | undefined) ?? null;
      const normalized = raw?.toLowerCase() ?? '';
      return (
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-zinc-700">Indicator color</label>
          <div className="flex flex-wrap items-center gap-2">
            {COLOR_PRESETS.map((preset) => {
              const active = normalized === preset.value.toLowerCase();
              return (
                <button
                  key={preset.value}
                  type="button"
                  title={preset.label}
                  onClick={() => field.onChange(preset.value)}
                  disabled={disabled}
                  className={
                    'h-6 w-6 rounded-full border transition-colors ' +
                    (active
                      ? 'ring-2 ring-zinc-900 ring-offset-1'
                      : 'border-zinc-200 hover:border-zinc-400')
                  }
                  style={{ backgroundColor: preset.value }}
                  aria-pressed={active}
                />
              );
            })}
            <UzInput
              name={`${name}-hex`}
              value={raw ?? ''}
              onChange={(e) => {
                const v = e.target.value.trim();
                field.onChange(v === '' ? null : v);
              }}
              placeholder="#3b82f6"
              disabled={disabled}
              containerClassName="w-32"
            />
            {raw && (
              <button
                type="button"
                onClick={() => field.onChange(null)}
                disabled={disabled}
                className="text-xs text-zinc-500 hover:text-zinc-700"
              >
                Clear
              </button>
            )}
          </div>
          {hint && <p className="text-xs text-zinc-500">{hint}</p>}
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      );
    }}
  </UzFormField>
);
